import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { useMediaQuery } from "@/shared/hooks/use-media-query"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogIcon,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@dalmatech/ui"
import Image from "next/image"
import * as styles from "./retrieve-quote-popup.styled"

type Props = {
  open: boolean
  petNames: string[]
  onRetrieve: () => void
  onStartNew: () => void
  isLoading?: boolean
}

export const RetrieveQuotePopup = ({ open, petNames, onRetrieve, onStartNew, isLoading }: Props) => {
  const { t, i18n } = useClientTranslation("global", { keyPrefix: "retrieve-quote-popup" })
  const isViewportBiggerThanMobile = useMediaQuery("(min-width: 768px)")
  const isManyPets = petNames.length > 1
  const joinedPetNames =
    typeof Intl !== "undefined" && Intl.ListFormat
      ? new Intl.ListFormat(i18n.language, { style: "long", type: "conjunction" }).format(petNames)
      : petNames.join(", ")

  const illustration = (
    <Image src="/img/retrieve-quote-illu.svg" alt="" width={114} height={128} />
  )
  const description = isManyPets
    ? t("description-many-pets", { first_pet: petNames[0] })
    : t("description", { pet_names: joinedPetNames })
  const buttons = (
    <div className={styles.buttonsWrapper}>
      <Button variant="secondary" size="lg" isFullWidth onClick={onStartNew}>
        {t("start-new")}
      </Button>
      <Button size="lg" isFullWidth onClick={onRetrieve} isLoading={isLoading}>
        {t("retrieve")}
      </Button>
    </div>
  )

  if (!isViewportBiggerThanMobile) {
    return (
      <>
        {open && <div aria-hidden="true" className={styles.backdrop} />}
        {/* dismissible=false blocks vaul's swipe/tap auto-close. On mobile
            Safari with modal=false, vaul can spuriously fire onOpenChange(false)
            (visibility change, overlay tap), which would delete the cookie and
            flash the popup closed. User must now close via the explicit X button. */}
        <Drawer open={open} modal={false} dismissible={false}>
          <DrawerContent className={styles.drawerContent} onClose={onStartNew}>
            <div className={styles.illustrationWrapper}>{illustration}</div>
            <DrawerTitle className={styles.title}>{t("title")}</DrawerTitle>
            <DrawerDescription className={styles.description}>{description}</DrawerDescription>
            {buttons}
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <>
      {open && <div aria-hidden="true" className={styles.backdrop} />}
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onStartNew()} modal={false}>
        <DialogContent
          className={styles.dialogContent}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogIcon>{illustration}</DialogIcon>
          <DialogTitle className={styles.title}>{t("title")}</DialogTitle>
          <DialogDescription className={styles.description}>{description}</DialogDescription>
          {buttons}
        </DialogContent>
      </Dialog>
    </>
  )
}
